import toast from "react-hot-toast";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import { paths } from "../routes/paths";
import Icon from "../components/icons/Icon";
import cecisCheckLogo from "../assets/cecis-check.svg";
import coopersystemMark from "../assets/coopersystem-mark-light.svg";
import "./Login.css";

function Login() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState();
  const [passwordError, setPasswordError] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // ProtectedRoute guarda o destino pretendido em state.from; sem ele, o
  // login sempre cai em /projects.
  const origin = location.state?.from;
  const destination = origin?.pathname ? `${origin.pathname}${origin.search || ""}` : paths.projects;

  const validateEmail = (email) => {
    if (!email.trim()) return "Campo obrigatório";
    const regex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
    if (!regex.test(email)) return "E-mail inválido";
  };

  const validatePassword = (password) => {
    if (!password) return "Campo obrigatório";
  };

  const validateFields = async () => {
    if (loading) return;

    setLoading(true);
    setSubmitting(true);
    setLoginError(false);

    const validatedEmail = validateEmail(email);
    const validatedPassword = validatePassword(password);

    setEmailError(validatedEmail || "");
    setPasswordError(validatedPassword || "");

    if (validatedEmail || validatedPassword) {
      setLoading(false);
      setSubmitting(false);
      return;
    }
    try {
      await toast.promise(signIn(email, password), {
        loading: "Realizando login...",
        success: "Login realizado com sucesso!",
        error: "Ocorreu um erro ao realizar login. Tente novamente!",
      });
      setTimeout(() => {
        navigate(destination, { replace: true });
      }, 2000);
    } catch (err) {
      console.error(err);
      setLoginError(true);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="login">
      <aside className="brand">
        <div className="brand-core">
          <img className="brand-logo" src={cecisCheckLogo} alt="Cecis Check" />
          <p className="brand-thesis">Consistência de dados em sprints do Redmine.</p>
        </div>

        <div className="brand-foot">
          <div className="brand-lockup">
            <img src={coopersystemMark} alt="" />
            <span>Coopersystem</span>
          </div>
        </div>
      </aside>

      <main className="pane">
        <form className="form">
          <h1 className="form-title">Entrar na sua conta</h1>
          <p className="form-sub">Use sua conta corporativa Coopersystem.</p>

          {loginError && (
            <div className="alert alert-error form-alert" role="alert">
              <div className="alert-icon" aria-hidden="true">!</div>
              <div>
                <b className="alert-title">E-mail ou senha incorretos</b>
                Confira os dados e tente novamente.
              </div>
            </div>
          )}

          <div className="form-field">
            <label className="field-label" htmlFor="email">E-mail</label>
            <input
              className={`input${emailError || loginError ? " input-error" : ""}`}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              spellCheck="false"
              autoFocus
              placeholder="nome.sobrenome@coopersystem.com.br"
              aria-invalid={Boolean(emailError)}
              aria-describedby={emailError ? "email-error" : undefined}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  document.getElementById("password")?.focus();
                }
              }}
            />
            <div className="field-error" id="email-error">{emailError}</div>
          </div>

          <div className="form-field">
            <label className="field-label" htmlFor="password">Senha</label>
            <div className="password-wrap">
              <input
                className={`input${passwordError || loginError ? " input-error" : ""}`}
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Digite sua senha"
                aria-invalid={Boolean(passwordError)}
                aria-describedby={passwordError ? "password-error" : undefined}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") validateFields();
                }}
              />
              <button
                type="button"
                className="eye-btn"
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                onClick={() => setShowPassword((current) => !current)}
              >
                <Icon name={showPassword ? "eye-off" : "eye"} />
              </button>
            </div>
            <div className="field-error" id="password-error">{passwordError}</div>
          </div>

          <div className="form-recover">
            <a
              className="recover-link"
              href="https://passwordreset.microsoftonline.com/passwordreset#!/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Esqueci minha senha <Icon name="external-link" strokeWidth={2.5} />
            </a>
          </div>

          <button
            type="button"
            className={loading ? "btn btn-disabled submit" : "btn btn-primary submit"}
            disabled={loading}
            onClick={validateFields}
          >
            {submitting && <span className="spinner" aria-hidden="true" />}
            {submitting ? "Entrando..." : "Entrar"}
          </button>
        </form>
      </main>
    </div>
  );
}

export default Login;
